import { snapshotMonthly, type SavedImage, type Server } from "@cloudserver/contracts";
import { Link } from "@tanstack/react-router";
import { Archive, Camera } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCanWrite, useReadOnly } from "../../api/users.js";
import { useImages } from "../../api/images.js";
import { progressOf, useRunningOperations } from "../../api/operations.js";
import { useCatalogue } from "../../api/queries.js";
import { runningByServer, useServers } from "../../api/servers.js";
import { ErrorMessage } from "../../ui/feedback.js";
import { money, relativeTime } from "../../ui/format.js";
import { FieldHint } from "../../ui/help.js";
import { EmptyState, PageHeader, Tabs } from "../../ui/layout.js";
import { Alert, Badge, Button, Card } from "../../ui/primitives.js";
import { DataTable } from "../../ui/table.js";
import {
  backupMonthly,
  gigabytes,
  ImageDialogs,
  OsLine,
  snapshotPrice,
  StatusBadge,
  useImageMenu,
  type ImageDialog,
} from "./shared.js";

export const IMAGE_TABS = ["snapshots", "backups"] as const;
export type ImageTab = (typeof IMAGE_TABS)[number];

/** The server an image names: a link while it exists, its old name with a note once it is gone. */
function ServerCell({
  projectId,
  server,
}: {
  projectId: string;
  server: { id: string | null; name: string } | null;
}) {
  const { t } = useTranslation();
  if (!server) return <span className="text-muted">—</span>;
  if (!server.id)
    return (
      <span className="flex flex-col">
        <span className="break-all">{server.name}</span>
        <span className="text-xs text-muted">{t("images.serverGone")}</span>
      </span>
    );
  return (
    <Link
      to="/projects/$projectId/servers/$serverId"
      params={{ projectId, serverId: server.id }}
      search={{ tab: "backups" }}
      className="break-all text-accent hover:underline"
    >
      {server.name}
    </Link>
  );
}

function DescriptionCell({ image }: { image: SavedImage }) {
  return (
    <span className="flex min-w-0 flex-col gap-0.5">
      <span className="flex flex-wrap items-center gap-1.5">
        <span className="break-words font-medium">{image.description || "—"}</span>
        <StatusBadge image={image} />
      </span>
      <OsLine image={image} />
    </span>
  );
}

/** Totals above the lists: what snapshots and backups cost this project per month. */
function Costs({
  projectId,
  images,
  servers,
}: {
  projectId: string;
  images: SavedImage[];
  servers: Server[];
}) {
  const { t, i18n } = useTranslation();
  const prices = useCatalogue(projectId, "prices").data;
  const types = useCatalogue(projectId, "server-types").data;
  if (!prices) return null;
  const fmt = (amount: number) => money(String(amount), prices.currency, i18n.language);
  const snapshots = images.filter((i) => i.kind === "snapshot");
  const stored = snapshots.reduce((sum, i) => sum + (i.sizeGb ?? 0), 0);
  const snapshotCost = snapshots.reduce(
    (sum, i) => sum + (snapshotMonthly(i.sizeGb, prices.snapshotPerGbMonth.net) ?? 0),
    0,
  );
  const withBackups = servers.filter((s) => s.backupWindow !== null);
  const backupCost = withBackups.reduce(
    (sum, s) => sum + (backupMonthly(s, types, prices) ?? 0),
    0,
  );
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <Card className="flex flex-col gap-1">
        <span className="flex items-center gap-1.5 text-sm text-muted">
          <Camera className="size-4" aria-hidden />
          {t("images.costSnapshots")}
          <FieldHint text={t("images.costSnapshotsHint")} topic="images" />
        </span>
        <span className="text-lg font-semibold tabular-nums">
          {t("images.perMonth", { price: fmt(snapshotCost) })}
        </span>
        <span className="text-xs text-muted">
          {t("images.costSnapshotsLine", {
            count: snapshots.length,
            size: gigabytes(stored, i18n.language),
            price: money(prices.snapshotPerGbMonth.net, prices.currency, i18n.language, 4),
          })}
        </span>
      </Card>
      <Card className="flex flex-col gap-1">
        <span className="flex items-center gap-1.5 text-sm text-muted">
          <Archive className="size-4" aria-hidden />
          {t("images.costBackups")}
          <FieldHint text={t("images.costBackupsHint")} topic="images" />
        </span>
        <span className="text-lg font-semibold tabular-nums">
          {t("images.perMonth", { price: fmt(backupCost) })}
        </span>
        <span className="text-xs text-muted">
          {t("images.costBackupsLine", {
            count: withBackups.length,
            percent: Number(prices.backupPercent),
          })}
        </span>
      </Card>
    </div>
  );
}

/** Servers and whether backups are on, with a link to turn them on or off. */
function BackupServers({ projectId, servers }: { projectId: string; servers: Server[] }) {
  const { t } = useTranslation();
  if (servers.length === 0) return null;
  return (
    <Card className="flex flex-col gap-3">
      <div className="flex flex-col gap-0.5">
        <h2 className="text-base font-semibold">{t("images.backupServers")}</h2>
        <p className="text-sm text-muted">{t("images.backupServersLead")}</p>
      </div>
      <ul className="flex flex-col divide-y divide-line">
        {servers.map((s) => (
          <li key={s.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
            <span className="flex min-w-0 flex-col">
              <Link
                to="/projects/$projectId/servers/$serverId"
                params={{ projectId, serverId: s.id }}
                search={{ tab: "backups" }}
                className="break-all text-sm font-medium text-accent hover:underline"
              >
                {s.name}
              </Link>
              {s.backupWindow && (
                <span className="text-xs text-muted">
                  {t("servers.overview.backupsOn", { window: s.backupWindow })}
                </span>
              )}
            </span>
            <Badge tone={s.backupWindow ? "ok" : "neutral"}>
              {s.backupWindow ? t("servers.backups.on") : t("servers.backups.off")}
            </Badge>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/**
 * `/projects/$projectId/images?tab=` — the project's snapshots and backups:
 * what each costs, where it came from, and the actions on it (restore a
 * server, start a new one, edit, protect, keep a backup, delete).
 */
export function ImagesPage({ projectId, tab }: { projectId: string; tab: ImageTab }) {
  const { t, i18n } = useTranslation();
  const images = useImages(projectId);
  const servers = useServers(projectId).data ?? [];
  const prices = useCatalogue(projectId, "prices").data;
  const running = useRunningOperations(projectId).data;
  const [dialog, setDialog] = useState<ImageDialog>(null);
  const { readOnly, reason: readOnlyReason, token: tokenReadOnly } = useReadOnly(projectId);
  // Guests may only look (docs/users.md): no create action; the API refuses them anyway.
  const canWrite = useCanWrite(projectId);
  const menu = useImageMenu(projectId, setDialog);
  const busyServers = new Set(runningByServer(running).keys());
  const rows = images.data?.filter((i) => i.kind === (tab === "snapshots" ? "snapshot" : "backup"));

  const snapshotButton = (
    <Button
      onClick={() => setDialog({ kind: "snapshot", serverId: null })}
      disabled={readOnly || servers.length === 0}
      icon={<Camera className="size-4" aria-hidden />}
    >
      {t("images.snapshotTitle")}
    </Button>
  );
  const common = {
    label: tab === "snapshots" ? t("images.tabs.snapshots") : t("images.tabs.backups"),
    rows,
    rowKey: (i: SavedImage) => i.id,
    initialSort: { key: "created", dir: "desc" as const },
    progress: (i: SavedImage) => progressOf(running, i.id),
    muted: (i: SavedImage) => i.status === "unavailable",
    rowMenu: (i: SavedImage) => menu(i, progressOf(running, i.id) !== null),
  };
  const sizeColumn = {
    key: "size",
    header: (
      <span className="inline-flex items-center gap-1">
        {t("images.size")}
        <FieldHint text={t("images.sizeHint")} topic="images" />
      </span>
    ),
    align: "right" as const,
    sort: (i: SavedImage) => i.sizeGb ?? 0,
    cell: (i: SavedImage) => (
      <span className="flex flex-col items-end whitespace-nowrap">
        <span className="tabular-nums">{gigabytes(i.sizeGb, i18n.language)}</span>
        {i.diskGb > 0 && (
          <span className="text-xs text-muted">{t("images.disk", { disk: i.diskGb })}</span>
        )}
      </span>
    ),
  };
  const createdColumn = {
    key: "created",
    header: t("images.created"),
    sort: (i: SavedImage) => i.createdAt,
    cell: (i: SavedImage) => (
      <span className="whitespace-nowrap text-muted" title={i.createdAt}>
        {relativeTime(i.createdAt, i18n.language)}
      </span>
    ),
  };

  return (
    <>
      <PageHeader
        title={t("nav.images")}
        intro={t("images.intro")}
        help="images"
        actions={canWrite ? snapshotButton : undefined}
      />
      {tokenReadOnly && <Alert tone="warn">{readOnlyReason}</Alert>}
      {images.data && <Costs projectId={projectId} images={images.data} servers={servers} />}
      <Tabs
        label={t("nav.images")}
        active={tab}
        items={IMAGE_TABS.map((key) => ({
          key,
          label: t(`images.tabs.${key}`),
          link: { to: "/projects/$projectId/images", params: { projectId }, search: { tab: key } },
        }))}
      />
      {images.error ? (
        <ErrorMessage error={images.error} />
      ) : tab === "snapshots" ? (
        <DataTable<SavedImage>
          {...common}
          empty={
            <EmptyState
              icon={<Camera className="size-6" aria-hidden />}
              title={t("images.emptySnapshots")}
              action={canWrite && servers.length > 0 ? snapshotButton : undefined}
              help="images"
            >
              {t("images.emptySnapshotsLead")}
            </EmptyState>
          }
          columns={[
            {
              key: "description",
              header: t("images.description"),
              primary: true,
              // Descriptions are free text: keep them readable next to the other columns.
              className: "min-w-48",
              sort: (i) => i.description,
              cell: (i) => <DescriptionCell image={i} />,
            },
            {
              key: "server",
              header: t("images.from"),
              sort: (i) => i.createdFrom?.name ?? "",
              cell: (i) => <ServerCell projectId={projectId} server={i.createdFrom} />,
            },
            sizeColumn,
            {
              key: "price",
              header: t("images.price"),
              align: "right",
              sort: (i) => i.sizeGb ?? 0,
              cell: (i) => (
                <span className="whitespace-nowrap tabular-nums">
                  {snapshotPrice(i, prices, i18n.language)}
                </span>
              ),
            },
            {
              key: "flags",
              header: t("images.flags"),
              hideOnPhone: true,
              cell: (i) => (
                <span className="flex flex-wrap gap-1">
                  {i.protection.delete && <Badge tone="ok">{t("images.protected")}</Badge>}
                  {Object.entries(i.labels).map(([k, v]) => (
                    <Badge key={k}>{v ? `${k}=${v}` : k}</Badge>
                  ))}
                  {!i.protection.delete && Object.keys(i.labels).length === 0 && (
                    <span className="text-muted">—</span>
                  )}
                </span>
              ),
            },
            createdColumn,
          ]}
        />
      ) : (
        <div className="flex flex-col gap-4">
          <DataTable<SavedImage>
            {...common}
            empty={
              <EmptyState
                icon={<Archive className="size-6" aria-hidden />}
                title={t("images.emptyBackups")}
                help="images"
              >
                {t("images.emptyBackupsLead")}
              </EmptyState>
            }
            columns={[
              {
                key: "description",
                header: t("images.description"),
                primary: true,
                // Descriptions are free text: keep them readable next to the other columns.
                className: "min-w-48",
                sort: (i) => i.description,
                cell: (i) => <DescriptionCell image={i} />,
              },
              {
                key: "server",
                header: t("images.belongsTo"),
                sort: (i) => i.server?.name ?? "",
                cell: (i) => <ServerCell projectId={projectId} server={i.server} />,
              },
              sizeColumn,
              createdColumn,
            ]}
          />
          <BackupServers projectId={projectId} servers={servers} />
        </div>
      )}
      <ImageDialogs
        projectId={projectId}
        dialog={dialog}
        servers={servers}
        busyIds={busyServers}
        prices={prices}
        onClose={() => setDialog(null)}
      />
    </>
  );
}
